import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/color-picker.index';
import { getPaletteColor } from '@/core/lib/colors';
import { useCreateOrganizationUnit } from '../hooks/useCreateOrganizationUnit';
import { useUpdateOrganizationUnit } from '../hooks/useUpdateOrganizationUnit';
import { useOrganizationUnits } from '../hooks/useOrganizationUnits';
import { useToast } from '@/shared/hooks/useToast';
import { createOrganizationUnitSchema, type CreateOrganizationUnitInput } from '../schemas';
import type { OrganizationUnit } from '../types';
import { OrganizationUnitIconPicker, OrganizationUnitIcon } from './OrganizationUnitIconPicker';

const NO_PARENT_VALUE = '__none__';

interface OrganizationUnitFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unit?: OrganizationUnit;
  parentId?: string | null;
}

export function OrganizationUnitFormDialog({
  open,
  onOpenChange,
  unit,
  parentId,
}: OrganizationUnitFormDialogProps) {
  const { t } = useTranslation('organizationUnits');
  const schema = createOrganizationUnitSchema(t);
  const createUnit = useCreateOrganizationUnit();
  const updateUnit = useUpdateOrganizationUnit();
  const { data: units = [] } = useOrganizationUnits();
  const { toast } = useToast();

  const isEditing = !!unit;
  const isPending = createUnit.isPending || updateUnit.isPending;
  const isPreSelectedParent = !!parentId && !unit;

  const { control, handleSubmit, reset, setValue, watch } = useForm<CreateOrganizationUnitInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      icon: '',
      color: 'indigo-deep',
      parentId: NO_PARENT_VALUE,
    },
  });

  const selectedIcon = watch('icon');
  const selectedColor = watch('color');

  useEffect(() => {
    if (open) {
      reset(
        unit
          ? {
              name: unit.name,
              description: unit.description ?? '',
              icon: unit.icon,
              color: unit.color,
              parentId: unit.parentId ?? NO_PARENT_VALUE,
            }
          : {
              name: '',
              description: '',
              icon: 'Building2',
              color: 'indigo-deep',
              parentId: parentId ?? NO_PARENT_VALUE,
            },
      );
    }
  }, [open, unit, parentId, reset]);

  const onSubmit = async (data: CreateOrganizationUnitInput) => {
    const payload = {
      ...data,
      parentId: data.parentId === NO_PARENT_VALUE ? null : (data.parentId ?? null),
    };

    try {
      if (isEditing) {
        await updateUnit.mutateAsync({ unitId: unit.id, payload });
        toast.success({ title: t('toast.updated') });
      } else {
        await createUnit.mutateAsync(payload);
        toast.success({ title: t('toast.created') });
      }
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const availableParents = units.filter((u) => u.id !== unit?.id);
  const colorValue = selectedColor ? getPaletteColor(selectedColor)?.value : undefined;

  const parentOptions = [
    { value: NO_PARENT_VALUE, label: t('dialog.noParent') },
    ...availableParents.map((u) => ({
      value: u.id,
      label: `${'—'.repeat(u.depth)} ${u.name}`,
    })),
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('dialog.editTitle') : t('dialog.createTitle')}</DialogTitle>
          <DialogDescription className="sr-only">{t('dialog.description')}</DialogDescription>
        </DialogHeader>

        <form
          id="organization-unit-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5 py-2"
        >
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">{t('dialog.icon')}</label>
              <OrganizationUnitIconPicker
                value={selectedIcon}
                onSelect={(icon) => setValue('icon', icon, { shouldValidate: true })}
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-10 w-10"
                    disabled={isPending}
                  >
                    {selectedIcon ? (
                      <OrganizationUnitIcon name={selectedIcon} className="h-5 w-5" />
                    ) : (
                      <span className="text-xs">{t('dialog.selectIcon')}</span>
                    )}
                  </Button>
                }
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">{t('dialog.color')}</label>
              <ColorPickerPopover
                value={selectedColor}
                onSelect={(color) => setValue('color', color, { shouldValidate: true })}
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 w-10 p-0"
                    disabled={isPending}
                  >
                    {colorValue && (
                      <div className="h-6 w-6 rounded-full" style={{ background: colorValue }} />
                    )}
                  </Button>
                }
              />
            </div>
          </div>

          <FormFieldInput
            control={control}
            name="name"
            label={t('dialog.name')}
            floatingLabel
            disabled={isPending}
            data-testid="organization-units-name-input"
          />

          <FormFieldTextarea
            control={control}
            name="description"
            label={t('dialog.description')}
            rows={3}
            disabled={isPending}
            data-testid="organization-units-description-input"
          />

          <div className="flex flex-col gap-1">
            <FormFieldSelect
              control={control}
              name="parentId"
              label={t('dialog.parent')}
              floatingLabel
              disabled={isPending || isPreSelectedParent}
              options={parentOptions}
            />
            {isPreSelectedParent && (
              <p className="text-xs text-muted-foreground">{t('dialog.parentPreselected')}</p>
            )}
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={isPending}
          >
            {t('dialog.cancel')}
          </Button>
          <Button type="submit" form="organization-unit-form" disabled={isPending}>
            {isPending ? t('dialog.submitting') : t('dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
